import { useState } from 'react';
import { MessageCircle, X, Send, Loader, Copy, Check, CreditCard, Smartphone } from 'lucide-react';

const WhatsAppSender = ({ bill, restaurantInfo, onClose }) => {
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [includeGooglePay, setIncludeGooglePay] = useState(true);
  const [copied, setCopied] = useState(false);

  const generateUPILink = () => {
    const merchantName = restaurantInfo?.name || 'Restaurant';
    const amount = bill.totalAmount.toFixed(2);
    // Fixed: Use the correct UPI ID from restaurantInfo, with proper fallback
    const upiId = restaurantInfo?.upiId || 'contact.wazwan@okhdfcbank'; 
    const transactionNote = `Bill #${bill.billNumber} - ${merchantName}`;
    const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(merchantName)}&am=${amount}&tn=${encodeURIComponent(transactionNote)}&cu=INR`;
    return upiUrl;
  };

  // Fixed: Simplified Google Pay link generation
  const generateGooglePayWebLink = () => {
    const merchantName = restaurantInfo?.name || 'Restaurant';
    const amount = bill.totalAmount.toFixed(2);
    const upiId = restaurantInfo?.upiId || 'contact.wazwan@okhdfcbank';
    
    // Fixed: Use standard Google Pay web URL format
    const gpayUrl = `https://pay.google.com/about/business/static/a/pay/?pa=${upiId}&pn=${encodeURIComponent(merchantName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(`Bill ${bill.billNumber}`)}`;
    
    return gpayUrl;
  };

  // Alternative PhonePe link generator
  const generatePhonePeLink = () => {
    const merchantName = restaurantInfo?.name || 'Restaurant';
    const amount = bill.totalAmount.toFixed(2);
    const upiId = restaurantInfo?.upiId || 'contact.wazwan@okhdfcbank';
    
    const phonePeUrl = `phonepe://pay?pa=${upiId}&pn=${encodeURIComponent(merchantName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(`Bill ${bill.billNumber}`)}`;
    
    return phonePeUrl;
  };

  const generateEnhancedBillMessage = () => {
    const restaurantName = restaurantInfo?.name || 'Restaurant';
    const billDate = new Date(bill.createdAt).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    let billText = `🧾 *DIGITAL BILL RECEIPT*\n`;
    billText += `🏪 *${restaurantName}*\n`;
    if (restaurantInfo?.address) {
      billText += `📍 ${restaurantInfo.address}\n`;
    }
    if (restaurantInfo?.phoneNumber) {
      billText += `📞 ${restaurantInfo.phoneNumber}\n`;
    }
    billText += `\n━━━━━━━━━━━━━━━━━━━━━\n`;
    billText += `📋 *Bill #${bill.billNumber}*\n`;
    billText += `📅 ${billDate}\n`;
    if (bill.tableNumber) {
      billText += `🪑 Table: ${bill.tableNumber}\n`;
    }
    if (bill.names) {
      billText += `👤 Customer: ${bill.names}\n`;
    }
    billText += `━━━━━━━━━━━━━━━━━━━━━\n\n`;

    // Items with better formatting
    billText += `🛍️ *ORDER DETAILS:*\n`;
    bill.items?.forEach((item, index) => {
      billText += `${index + 1}. *${item.itemName}*\n`;
      billText += `   ${item.quantity} × ₹${item.price.toFixed(2)} = *₹${(item.quantity * item.price).toFixed(2)}*\n\n`;
    });

    billText += `━━━━━━━━━━━━━━━━━━━━━\n`;
    billText += `💰 *PAYMENT SUMMARY:*\n`;
    billText += `📊 Subtotal: ₹${bill.subtotal?.toFixed(2) || '0.00'}\n`;

    if (bill.cgst > 0) {
      billText += `🏛️ CGST (5%): ₹${bill.cgst.toFixed(2)}\n`;
    }
    if (bill.sgst > 0) {
      billText += `🏛️ SGST (5%): ₹${bill.sgst.toFixed(2)}\n`;
    }
    if (bill.discountAmount > 0) {
      billText += `🎟️ Discount (${bill.discountPercentage}%): -₹${bill.discountAmount.toFixed(2)}\n`;
    }

    billText += `━━━━━━━━━━━━━━━━━━━━━\n`;
    billText += `💳 *TOTAL AMOUNT: ₹${bill.totalAmount.toFixed(2)}*\n`;
    billText += `━━━━━━━━━━━━━━━━━━━━━\n\n`;

    // Payment options section
    if (includeGooglePay) {
      billText += `📱 *QUICK PAYMENT OPTIONS:*\n\n`;

      // Fixed: Show the actual UPI ID being used
      const currentUpiId = restaurantInfo?.upiId || 'contact.wazwan@okhdfcbank';
      
      billText += `💚 *Pay with UPI Apps:*\n`;
      billText += `🔗 Click to Pay: ${generateUPILink()}\n\n`;

      billText += `💡 *Manual Payment Details:*\n`;
      billText += `🆔 UPI ID: ${currentUpiId}\n`;
      billText += `💰 Amount: ₹${bill.totalAmount.toFixed(2)}\n`;
      billText += `📝 Note: Bill #${bill.billNumber}\n\n`;

      billText += `📲 *How to Pay:*\n`;
      billText += `1️⃣ Open any UPI app (Google Pay, PhonePe, Paytm)\n`;
      billText += `2️⃣ Click the payment link above OR\n`;
      billText += `3️⃣ Scan QR code or enter UPI ID manually\n`;
      billText += `4️⃣ Enter amount: ₹${bill.totalAmount.toFixed(2)}\n`;
      billText += `5️⃣ Add note: Bill #${bill.billNumber}\n`;
      billText += `6️⃣ Complete payment\n\n`;

      billText += `━━━━━━━━━━━━━━━━━━━━━\n`;
    }

    billText += `✅ *Status: ${bill.status}*\n`;
    billText += `💳 Payment Method: ${bill.paymentMethod || 'Pending'}\n`;

    if (restaurantInfo?.gstin) {
      billText += `🏛️ GSTIN: ${restaurantInfo.gstin}\n`;
    }
    billText += `\n🙏 *Thank you for dining with us!*\n`;
    billText += `⭐ Please rate your experience\n`;
    billText += `📱 Visit again soon!\n\n`;
    billText += `🔄 For any queries, reply to this message`;
    return billText;
  };

  const copyUPILink = async () => {
    try {
      await navigator.clipboard.writeText(generateUPILink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleSendWhatsApp = () => {
    setSending(true);

    try {
      const billMessage = message || generateEnhancedBillMessage();
      const cleanMobileNumber = bill.mobileNumber.replace(/\D/g, '');

      let whatsappNumber = cleanMobileNumber;
      if (cleanMobileNumber.length === 10) {
        whatsappNumber = '91' + cleanMobileNumber;
      }

      const encodedMessage = encodeURIComponent(billMessage);
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

      window.open(whatsappUrl, '_blank');

      setTimeout(() => {
        setSending(false);
        onClose();
      }, 1000);

    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      alert('Error: Failed to open WhatsApp. Please try again.');
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 flex justify-between items-center text-white">
          <div className="flex items-center">
            <MessageCircle size={24} className="mr-3" />
            <div>
              <h3 className="text-lg font-semibold">Send Digital Bill via WhatsApp</h3>
              <p className="text-green-100 text-sm">Bill #{bill.billNumber} • {bill.mobileNumber} • ₹{bill.totalAmount.toFixed(2)}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-white hover:text-green-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row h-[calc(95vh-5rem)]">
          <div className="lg:w-1/2 p-6 border-r border-gray-200 overflow-y-auto">
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <CreditCard className="mr-2" size={20} />
                Bill Preview
              </h4>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <p><strong className="text-gray-700">Bill Number:</strong> <span className="text-blue-600">#{bill.billNumber}</span></p>
                    <p><strong className="text-gray-700">Date:</strong> {new Date(bill.createdAt).toLocaleDateString('en-IN')}</p>
                    <p><strong ClassName="text-gray-700">Customer:</strong> {bill.names || 'Walk-in Customer'}</p>
                  </div>
                  <div className="space-y-2">
                    <p><strong className="text-gray-700">Mobile:</strong> <span className="text-green-600">{bill.mobileNumber}</span></p>
                    <p><strong className="text-gray-700">Total Amount:</strong> <span className="text-2xl font-bold text-green-600">₹{bill.totalAmount.toFixed(2)}</span></p>
                    <p><strong className="text-gray-700">Payment:</strong> {bill.paymentMethod}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <Smartphone className="mr-2" size={20} />
                Payment Integration
              </h4>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="includeGooglePay"
                      checked={includeGooglePay}
                      onChange={(e) => setIncludeGooglePay(e.target.checked)}
                      className="mr-3 h-4 w-4 text-green-600"
                    />
                    <label htmlFor="includeGooglePay" className="font-medium text-gray-700">
                      Include Digital Payment Links
                    </label>
                  </div>
                  <div className="text-green-600 font-semibold">Recommended</div>
                </div>

                {includeGooglePay && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h5 className="font-medium text-gray-700">Quick Payment Options:</h5>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-green-600 font-bold text-sm">UPI</span>
                          </div>
                          <span className="font-medium">Universal UPI Link</span>
                        </div>
                        <button
                          onClick={copyUPILink}
                          className="flex items-center px-3 py-1 text-sm bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                        >
                          {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                          {copied ? 'Copied!' : 'Copy Link'}
                        </button>
                      </div>

                      <div className="text-xs text-gray-500 px-3 break-all">
                        <strong>UPI Link:</strong> {generateUPILink()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h5 className="font-medium text-yellow-800 mb-2">💡 Current UPI Setup</h5>
              <p className="text-yellow-700 text-sm">
                <strong>Active UPI ID:</strong> {restaurantInfo?.upiId || 'contact.wazwan@okhdfcbank'}
              </p>
              <p className="text-yellow-600 text-xs mt-1">
                {restaurantInfo?.upiId ? '✅ Using your configured UPI ID' : '⚠️ Using default UPI ID - Update in restaurant settings'}
              </p>
            </div>
          </div>

          <div className="lg:w-1/2 p-6 overflow-y-auto">
            <div className="mb-4">
              <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <MessageCircle className="mr-2" size={20} />
                WhatsApp Message Preview
              </h4>
              <div className="bg-gray-50 rounded-lg p-1 mb-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <MessageCircle size={16} className="text-white" />
                    </div>
                    <div className="ml-3">
                      <div className="font-medium text-gray-800">{restaurantInfo?.name || 'Restaurant'}</div>
                      <div className="text-xs text-gray-500">WhatsApp Business</div>
                    </div>
                  </div>

                  <textarea
                    value={message || generateEnhancedBillMessage()}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full h-80 p-4 border border-gray-200 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-green-50"
                    placeholder="Enhanced bill message will appear here..."
                  />

                  <div className="flex justify-between items-center mt-3 text-xs text-gray-500">
                    <span>Message length: {(message || generateEnhancedBillMessage()).length} characters</span>
                    <span className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                      Ready to send
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-gray-500 text-xs mb-4">
                ✨ Enhanced with universal UPI links, clear payment instructions, and professional formatting
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleSendWhatsApp}
                disabled={sending}
                className="w-full py-4 px-6 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {sending ? (
                  <>
                    <Loader size={20} className="animate-spin mr-2" />
                    Opening WhatsApp...
                  </>
                ) : (
                  <>
                    <Send size={20} className="mr-2" />
                    Send Enhanced Bill via WhatsApp
                  </>
                )}
              </button>

              <button
                onClick={onClose}
                className="w-full py-3 px-6 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h5 className="font-medium text-blue-800 mb-2">🚀 Enhanced Features</h5>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Universal UPI payment links (works with all UPI apps)</li>
                  <li>• Professional bill formatting with emojis</li>
                  <li>• Step-by-step payment instructions</li>
                  <li>• Manual payment fallback options</li>
                </ul>
              </div>

              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-amber-800 text-sm">
                  <strong>🔧 Technical Note:</strong> Using standard UPI:// protocol for maximum compatibility. 
                  Works with Google Pay, PhonePe, Paytm, and all UPI apps.
                </p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <p className="text-green-800 text-sm">
                  <strong>💡 Pro Tip:</strong> The UPI link will automatically open the customer's preferred UPI app 
                  with pre-filled amount and merchant details!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSender;
